import {
  Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { BudgetVersion, Paginated, Project } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { formatMoney } from "../lib/money";

export const STATUS_BUDZETU: Record<
  string,
  { label: string; color: "default" | "info" | "success" | "error" | "warning" }
> = {
  draft: { label: "Roboczy", color: "default" },
  submitted: { label: "W akceptacji", color: "info" },
  // Stan roboczy dla kierownika i wiążący dla raportu naraz: plan liczy się
  // z pozycji zatwierdzonych, więc można na nich kontraktować.
  partially_approved: { label: "Zatwierdzony częściowo", color: "warning" },
  approved: { label: "Zatwierdzony", color: "success" },
  rejected: { label: "Odrzucony", color: "error" },
  locked: { label: "Zamknięty", color: "default" },
};

export function ProjectPage() {
  const { code = "" } = useParams();
  const { can } = useAuth();
  const nawiguj = useNavigate();
  const kolejka = useQueryClient();
  const [blad, setBlad] = useState<ApiError | null>(null);

  const { data: projekt } = useQuery({
    queryKey: ["project", code],
    queryFn: () => api.get<Project>(`/projects/${code}`),
  });
  const { data: wersje } = useQuery({
    queryKey: ["budgets", code],
    queryFn: () => api.get<Paginated<BudgetVersion>>(`/projects/${code}/budgets`),
  });

  const lista = wersje?.results ?? [];
  const ostatnia = lista[0];

  const nowa = useMutation({
    mutationFn: () =>
      api.post<BudgetVersion>(`/projects/${code}/budgets`, {
        label: "", copy_from: ostatnia?.id ?? null,
      }),
    onSuccess: (wersja) => {
      setBlad(null);
      kolejka.invalidateQueries({ queryKey: ["budgets", code] });
      nawiguj(`/projekty/${code}/kosztorys/${wersja.id}`);
    },
    // Bez tego nieudana próba nie zostawia po sobie śladu na ekranie —
    // użytkownik klika i nic się nie dzieje, co jest gorsze niż komunikat.
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="baseline" spacing={2}>
        <BackButton fallback="/projekty" />
        <Typography variant="h6">
          {projekt?.code} — {projekt?.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {projekt?.organization_name}
          {projekt?.account_plan_name ? ` · ${projekt.account_plan_name}` : ""}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button component={Link} to={`/projekty/${code}/obsada`} variant="outlined">
          Obsada
        </Button>
        <Button component={Link} to={`/projekty/${code}/umowy`} variant="outlined">
          Umowy
        </Button>
        <Button component={Link} to={`/projekty/${code}/koszty`} variant="outlined">
          Koszty
        </Button>
        <Button component={Link} to={`/projekty/${code}/raport`} variant="outlined">
          Raport czterech kolumn
        </Button>
        {can("budget.edit") && (
          <Button variant="contained" onClick={() => nowa.mutate()} disabled={nowa.isPending}>
            {ostatnia ? "Nowa wersja z kopii" : "Utwórz kosztorys"}
          </Button>
        )}
      </Stack>

      {blad && (
        <Alert severity="error" onClose={() => setBlad(null)}>
          {blad.message}
        </Alert>
      )}

      {lista.length === 0 && (
        <Alert severity="info">
          Ten projekt nie ma jeszcze kosztorysu.
        </Alert>
      )}

      {lista.length > 0 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Wersja</TableCell>
                <TableCell>Opis</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Pozycji</TableCell>
                <TableCell align="right">Suma netto</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((w) => (
                <TableRow key={w.id} hover>
                  <TableCell>v{w.number}</TableCell>
                  <TableCell>{w.label || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_BUDZETU[w.status]?.label ?? w.status}
                          color={STATUS_BUDZETU[w.status]?.color ?? "default"} />
                  </TableCell>
                  <TableCell align="right">{w.line_count}</TableCell>
                  <TableCell align="right">{formatMoney(w.total_net)}</TableCell>
                  <TableCell align="right">
                    <Button component={Link} to={`/projekty/${code}/kosztorys/${w.id}`}>
                      Otwórz
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
